import React from 'react';
import { Card, Typography, Divider } from 'antd';
import ImageComparator from 'components/Shared/components/imageComparator';
import ImageComparatorExample from 'components/Shared/components/imageComparator/ImageComparatorExample';
import { Container, ShowcaseSection, ComponentCard } from './styled';

const { Title, Paragraph } = Typography;

const DevShowcase: React.FC = () => {
  return (
    <Container>
      <Card>
        <Title level={1}>Development Component Showcase</Title>
        <Paragraph>
          This page is only visible in development mode and showcases components that are
          implemented but not yet integrated into the main application.
        </Paragraph>
      </Card>

      <ShowcaseSection>
        <ComponentCard>
          <Title level={2}>Image Comparator</Title>
          <Paragraph>
            A side-by-side image comparison tool with synchronized zoom controls, drag-and-drop
            upload, and interactive pan/zoom functionality.
          </Paragraph>
          <Divider />
          <ImageComparator />
          <Paragraph>
            <strong>1. Basic Usage:</strong>
          </Paragraph>
          <Card size="small" style={{ backgroundColor: '#f6f8fa', marginBottom: 16 }}>
            <pre style={{ margin: 0, fontSize: '12px' }}>
              {`import ImageComparator from './imageComparator';
import { Frame, SegmentationLabel } from 'models/segmentation';

const MyComponent = () => {
  const frameData: Frame = {
    dicomUuid: 'your-dicom-uuid',
    fileUrl: 'your-image-url',
    icid: 'your-frame-icid',
    index: 0,
    segmentationLabels: [],
  };

  const segmentations: SegmentationLabel[] = [
    {
      icid: 'segmentation-id',
      uuid: 'segmentation-uuid',
      createdAt: '2025-01-01T00:00:00Z',
      datasourceName: 'YOUR_DATASOURCE',
      // ... other required fields
      segmentationClassValue: 'Anatomical Structure',
      fileUrl: 'segmentation-overlay-url',
    },
  ];

  return (
    <ImageComparator
      leftFrame={frameData}
      rightFrame={frameData}
      leftSegmentations={segmentations}
      rightSegmentations={segmentations}
    />
  );
};`}
            </pre>
          </Card>

          <Paragraph>
            <strong>2. Data Structure Requirements:</strong>
          </Paragraph>
          <Card size="small" style={{ backgroundColor: '#f6f8fa', marginBottom: 16 }}>
            <pre style={{ margin: 0, fontSize: '12px' }}>
              {`// Frame interface requirements
interface Frame {
  dicomUuid: string;           // DICOM unique identifier
  fileUrl: string;             // URL to the medical image
  icid: string;                // Frame internal ID
  index: number;               // Frame index in sequence
  segmentationLabels: SegmentationLabel[];
}

// SegmentationLabel interface requirements
interface SegmentationLabel {
  icid: string;                // Segmentation ID
  uuid: string;                // Unique identifier
  createdAt: string;           // ISO date string
  datasourceName: string;      // Data source name
  dicomIcid: string;           // DICOM internal ID
  dicomUuid: string;           // DICOM UUID
  frameIcid: string;           // Frame internal ID
  frameIndex: number;          // Frame index
  frameUuid: string;           // Frame UUID
  isApproved: boolean;         // Approval status
  isFullyVisible: boolean;     // Visibility flag
  isRejected: boolean;         // Rejection status
  isReviewed: boolean;         // Review status
  paths: string[];             // SVG path data
  segmentationClassValue: string; // Anatomical structure name
  fileUrl?: string;            // Optional overlay image URL
}`}
            </pre>
          </Card>

          <Paragraph>
            <strong>3. Interactive Controls:</strong>
          </Paragraph>
          <Paragraph>
            <ul>
              <li>
                <strong>Zoom:</strong> Use zoom in/out buttons or Ctrl/Cmd + mouse wheel
              </li>
              <li>
                <strong>Pan:</strong> Click and drag to move the image around
              </li>
              <li>
                <strong>Reset:</strong> Reset zoom to original scale
              </li>
              <li>
                <strong>Fit to Screen:</strong> Auto-scale image to fit container
              </li>
              <li>
                <strong>Toggle Overlays:</strong> Click eye icons to show/hide segmentation overlays
              </li>
            </ul>
          </Paragraph>

          <Divider />
          <ImageComparatorExample />
        </ComponentCard>
      </ShowcaseSection>
    </Container>
  );
};

export default DevShowcase;
